import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "Stock Wordle is a daily puzzle where you try to identify a mystery publicly traded company using clues like its stock chart, financial metrics, and hints. It borrows the daily, single-puzzle format of Wordle but applies it to the stock market. The most popular version is Wallstreetle, which runs nine different categories of puzzles every day for free.";

const FAQS = [
  { q: 'Is Stock Wordle related to the original Wordle?', a: 'No, it just borrows the daily-puzzle format. Wordle (now owned by The New York Times) is about words. Stock Wordle is about identifying companies from financial data.' },
  { q: 'Where can I play Stock Wordle for free?', a: 'Wallstreetle.com is the original Stock Wordle. It offers nine categories, daily puzzles that reset at 02:00 UTC, and no signup required for the free puzzles.' },
  { q: 'How many guesses do you get?', a: 'On Wallstreetle you can guess unlimited times, but your score depends on how few hints and guesses you used. Strict 6-guess versions exist on copycat sites.' },
  { q: 'Is Stock Wordle good for learning about stocks?', a: 'Yes — that\'s the main appeal. Each puzzle forces you to read a real stock chart, scan financial metrics, and reason about which company fits. You learn through pattern recognition rather than memorising definitions.' },
  { q: 'Can I share my results?', a: 'Yes. After completing a puzzle, most Stock Wordle games generate a shareable score block — the same emoji-grid pattern Wordle made famous, adapted for stocks.' },
];

export default function StockWordlePage() {
  return (
    <BlogArticleLayout
      slug="stock-wordle"
      title="What Is Stock Wordle? The Daily Stock Guessing Game Explained"
      description="Stock Wordle is the daily puzzle for stock-market lovers. Here's how it works, why people play, and where to try one for free."
      keywords="stock wordle, stockle, stockdle, what is stock wordle, daily stock puzzle, finance wordle, stock guessing game, wallstreetle"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['finance-wordle', 'daily-stock-challenge', 'free-stock-market-games']}
      ctaText="Play Stock Wordle today"
      ctaSubtext="Wallstreetle is the original Stock Wordle. New mystery stock every day, across 9 categories. Free, no signup."
      ctaHref="/start"
      ctaLabel="Play Wallstreetle"
    >
      <section>
        <h2>What is Stock Wordle?</h2>
        <p>
          Stock Wordle is a daily puzzle game built around identifying a mystery public company. Each day, players are shown a real stock chart, a handful of financial metrics, and a series of hints, and asked to guess which company it is.
          The format borrows from Wordle: one puzzle per day, share the result, come back tomorrow.
        </p>
        <p>
          The original and most-played version is <Link to="/">Wallstreetle</Link>, which has run daily puzzles since 2024 across nine market categories.
        </p>
      </section>

      <section>
        <h2>How does Stock Wordle work?</h2>
        <p>
          The Wallstreetle format works like this:
        </p>
        <ul>
          <li><strong>1. Pick a category</strong> — Household Name, Tech, WallStreetBets, and more.</li>
          <li><strong>2. View the chart</strong> — a real, anonymised price history for the mystery company.</li>
          <li><strong>3. Read the metrics</strong> — market cap, revenue, P/E ratio, and other clues.</li>
          <li><strong>4. Use hints if stuck</strong> — industry, country, business description.</li>
          <li><strong>5. Guess the ticker or company name</strong>.</li>
          <li><strong>6. Share your score</strong> — fewer guesses and hints = higher score.</li>
        </ul>
      </section>

      <section>
        <h2>Why do people play Stock Wordle?</h2>
        <p>
          Three reasons consistently come up:
        </p>
        <ul>
          <li><strong>Daily ritual.</strong> One puzzle, five minutes. The same hook that made Wordle a habit.</li>
          <li><strong>Learning by doing.</strong> Reading real charts and ratios trains pattern recognition no textbook can match. See <Link to="/blog/learn-to-invest">our guide on learning to invest</Link>.</li>
          <li><strong>Competition.</strong> Leaderboards, streaks, and shareable scores give it social momentum.</li>
        </ul>
      </section>

      <section>
        <h2>What financial concepts does Stock Wordle teach?</h2>
        <p>
          Without trying, regular players pick up real fluency in:
        </p>
        <ul>
          <li><Link to="/blog/what-is-market-cap">Market capitalization</Link> and what counts as large vs small.</li>
          <li><Link to="/blog/what-is-pe-ratio">P/E ratios</Link> across sectors.</li>
          <li><Link to="/blog/revenue-vs-earnings">Revenue vs earnings</Link> and why they diverge.</li>
          <li>How a chart "shape" maps to a sector — utilities look different from biotech.</li>
        </ul>
      </section>

      <section>
        <h2>Where can I play?</h2>
        <p>
          Start with <Link to="/">Wallstreetle</Link>: free daily puzzles across 9 categories, plus side games like
          <Link to="/compare-the-pair"> Compare the Pair</Link> (head-to-head metric comparison) and <Link to="/trivia-rush">Trivia Rush</Link> (rapid-fire higher/lower).
          The puzzle resets at 02:00 UTC every day.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
